import { Component } from '@angular/core';
import { GridReadyEvent, GridApi, ColDef, GridOptions } from 'ag-grid-community';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { FormOverridesListAgGridDataSource, FormOverridesListDataSourceParams } from './agGridDataSource';
import { AgGridColumns, AgGridOptionsBuilder, AgGridFilterParams } from '../../../../AgGrid';
import { ReturnPreviewService } from '../returnPreview.service';
import { ReturnService } from '../../../return.service';
import { AgGridLinkCellRendererComponent } from '../../../../AgGrid/CellRenderers/agGridLinkCellRenderer.component';
import { AgGridLinkCellRendererParams } from '../../../../AgGrid/CellRenderers/agGridLinkCellRenderer.component';
import { ReturnPreviewFormOverridesListActionCellRendererParams, ReturnPreviewFormOverridesListActionCellRendererComponent } from './agGridActionCellRenderer.component';
import { IWeissmanModalComponent } from '../../../../WeissmanModalService';
import { MessageModalService } from '../../../../../UI-Lib/Message-Box/messageModal.service';

export interface ReturnPreviewFormOverrideListParams {
    formRevisionScheduleId: number;
    filingBatchId: number;
    returnIds: number[];
    isReadOnly: boolean;
}

export interface ReturnPreviewFormOverrideListResult {
    deletedOverrides: Compliance.ReturnFormOverrideDetailModel[];
    clickedOverride: Compliance.ReturnFormOverrideDetailModel;
}

@Component({
    selector: 'return-preview-form-override-list',
    templateUrl: './returnPreviewFormOverrideList.component.html',
    styleUrls: ['./returnPreviewFormOverrideList.component.scss'],
    standalone: false
})
export class ReturnPreviewFormOverrideListComponent implements IWeissmanModalComponent<ReturnPreviewFormOverrideListParams, ReturnPreviewFormOverrideListResult> {
    constructor(
        private readonly _bsModalRef: BsModalRef,
        private readonly _returnPreviewService: ReturnPreviewService,
        private readonly _returnService: ReturnService,
        private readonly _messageModalService: MessageModalService) {
    }

    params: ReturnPreviewFormOverrideListParams;
    result: ReturnPreviewFormOverrideListResult = {
        deletedOverrides: [],
        clickedOverride: null
    };

    gridApi: GridApi;
    gridTotalsRow: any;
    gridId: System.Guid = '499C77DA-CF06-4B3A-BC35-82BB541ADB80';
    gridOptions: GridOptions = new AgGridOptionsBuilder()
        .withColumnPinning()
        .buildDefault(this);

    private _gridDataSource: FormOverridesListAgGridDataSource;

    cancel(): void {
        this._bsModalRef.hide();
    }

    onAgGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;
        const actionButtonCount = this.params.isReadOnly ? 1 : 2;

        const columns: ColDef[] = [
            {
                headerName: 'Parcel',
                field: 'parcelAcctNumberDisplay',
                lockVisible: true,
                lockPosition: true,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnWidth,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    getHelpContentId: (params: AgGridLinkCellRendererParams) => 'app.view-parcel',
                    newWindow: true,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        const override = params.data as Compliance.ReturnFormOverrideDetailModel;
                        if (!(override && override.parcelId)) {
                            return '';
                        }
                        return `#/parcel/${override.parcelId}`;
                    },
                    isDisabled: (params: AgGridLinkCellRendererParams) => {
                        return false;
                    }
                } as AgGridLinkCellRendererParams
            },
            {
                headerName: 'Assessor',
                field: 'parcelAssessorName',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnWidth,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    getHelpContentId: (params: AgGridLinkCellRendererParams) => 'app.view-assessor',
                    newWindow: true,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        const override = params.data as Compliance.ReturnFormOverrideDetailModel;
                        if (!(override && override.parcelAssessorId)) {
                            return '';
                        }
                        return `#/assessor/${override.parcelAssessorId}`;
                    },
                    isDisabled: (params: AgGridLinkCellRendererParams) => {
                        return false;
                    }
                } as AgGridLinkCellRendererParams
            },
            {
                headerName: 'Form',
                field: 'formRevisionName',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnWidth
            },
            {
                headerName: 'Field',
                field: 'fieldNameDisplay',
                width: AgGridColumns.textColumnWidth,
                sortable: false
            },
            {
                headerName: 'Field Name',
                field: 'fieldName',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnWidth,
                hide: true
            },
            {
                headerName: 'Override Value',
                field: 'value',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnWidth
            },
            {
                headerName: '',
                field: 'actions',
                lockVisible: true,
                lockPosition: true,
                sortable: false,
                width: AgGridColumns.getActionColumnWidth(actionButtonCount),
                minWidth: AgGridColumns.getActionColumnWidth(actionButtonCount),
                maxWidth: AgGridColumns.getActionColumnWidth(actionButtonCount),
                suppressSizeToFit: true,
                suppressAutoSize: true,
                resizable: false,
                suppressColumnsToolPanel: true,
                lockPinned: true,
                pinned: 'right',
                cellRenderer: ReturnPreviewFormOverridesListActionCellRendererComponent,
                cellRendererParams: {
                    viewReturn: this._viewReturn.bind(this),
                    deleteOverride: this._deleteOverride.bind(this),
                    isReadOnly: this.params.isReadOnly
                } as ReturnPreviewFormOverridesListActionCellRendererParams
            }
        ];

        this.gridApi.setGridOption('columnDefs', columns);

        this.gridApi.applyColumnState({
            state: [{ colId: 'parcelAcctNumberDisplay', sort: 'asc' }],
            defaultState: { sort: null }
        });

        this._setDataSource();
        this.gridApi.sizeColumnsToFit();
    }

    private _refreshDataSource(): void {
        if (!this._gridDataSource) {
            const success = this._setDataSource();
            if (!success) {
                return;
            }
        }
        this._gridDataSource.refresh();
    }

    private _setDataSource(): boolean {
        if (!this.gridApi || this._gridDataSource) {
            return;
        }

        const dataSourceParams = (): FormOverridesListDataSourceParams => {
            return {
                returnIds: this._returnService.sharedState.returns.map(r => r.returnId)
            };
        };

        this._gridDataSource = new FormOverridesListAgGridDataSource(
            this.gridApi,
            this._returnPreviewService,
            dataSourceParams
        );

        this.gridApi.updateGridOptions({ datasource: this._gridDataSource });
        return true;
    }

    private async _viewReturn(params: AgGridLinkCellRendererParams): Promise<void> {
        this.result.clickedOverride = params.data as Compliance.ReturnFormOverrideDetailModel;
        this._bsModalRef.hide();
    }

    private async _deleteOverride(params: ReturnPreviewFormOverridesListActionCellRendererParams): Promise<void> {
        try {
            await this._messageModalService.confirm('Are you sure you wish to delete the override?', 'Confirm Delete');
        } catch (e) {
            return Promise.resolve();
        }

        const data = params.data as Compliance.ReturnFormOverrideDetailModel;

        const returnMergedParcelIds: Compliance.ReturnMergedParcelIdModel = {
            returnId: data.returnId,
            mergedParcelId: (this._returnService.isReturnConsolidated(data.returnId)) ? data.parcelId : null
        };

        const returnFormOverrideDeleteRequestModel: Compliance.ReturnFormOverrideRequestModel = {
            formRevisionId: data.formRevisionId,
            fieldName: data.fieldName,
            returnMergedParcelIds: [returnMergedParcelIds],
            value: null,
            rowVersion: data.rowVersion
        };

        await this._returnPreviewService.deleteFormFieldOverrides(returnFormOverrideDeleteRequestModel);

        this.result.deletedOverrides.push(data);
        this._refreshDataSource();
    }
}
